import React, { useEffect, useState } from "react";
import http from "services/http";
import PermissionGate from "utils/PermissionGate";
import dayjs from "dayjs";
import { alerta } from "components/Alert/index";
import formatMoney from "utils/formatMoney";
import Swal from "sweetalert2";
import {toast} from "react-toastify";
import {moedaInput, parseMoeda} from 'utils/moedaInput';

const FAIXAS = [
  { valor: 'dezena', rotulo: 'Dezena', intervalo: '00 a 99' },
  { valor: 'centena', rotulo: 'Centena', intervalo: '000 a 999' },
  { valor: 'milhar', rotulo: 'Milhar', intervalo: '0000 a 9999' },
  { valor: 'cem_mil', rotulo: '100 mil', intervalo: '00000 a 99999' },
];

const ROTULO_FAIXA = Object.fromEntries(FAIXAS.map((faixa) => [faixa.valor, faixa.rotulo]));

const ROTULO_STATUS = {
  em_andamento: { texto: 'Em andamento', classe: 'badge-success' },
  desativada: { texto: 'Desativada', classe: 'badge-secondary' },
  encerrada: { texto: 'Encerrada', classe: 'badge-danger' },
  cancelada: { texto: 'Cancelada', classe: 'badge-warning' },
};

const DIAS = [
  ['domingo', 'dom'], ['segunda', 'seg'], ['terca', 'ter'], ['quarta', 'qua'],
  ['quinta', 'qui'], ['sexta', 'sex'], ['sabado', 'sáb'],
];

const diasDaExtracao = (extracao) => DIAS.filter(([coluna]) => extracao[coluna]).map(([, rotulo]) => rotulo).join(', ');

// `error.response` é undefined em erro de rede: sem o fallback o alerta quebra.
const avisarErro = (erro, mensagem) => alerta(erro.response?.data ?? { message: mensagem });

// Declarado fora do componente de propósito (como no Bilhetinho): dentro do pai ele
// virava um tipo novo a cada render, e o React remontava o campo com a edição aberta.
function CampoEncerramento({ el, aoAlterar }) {
  const [editarData, setEditarData] = useState(false);
  const [data, setData] = useState(dayjs(el.data_limite).format('YYYY-MM-DDTHH:mm'));

  const salvar = async () => {
    try {
      const response = await http.patch('/rifas/' + el.id, { 'data_limite': data })
      setEditarData(false)
      alerta(response.data)
      aoAlterar()
    } catch (error) {
      avisarErro(error, 'Erro ao editar a rifa!')
    }
  }

  return (
      !editarData ?
      <>
        {dayjs(el.data_limite).format('DD/MM/YYYY HH:mm')}
        {!el.cancelado_em && (
          <PermissionGate permissions={['Alterar rifa']}>
            <button
              className="btn btn-secondary ml-1 p-0 text-center"
              style={{height: '25px', width: '25px'}}
              onClick={() => setEditarData(true)}
            >
              <i className="fa fa-pencil" aria-hidden="true" />
            </button>
          </PermissionGate>
        )}
      </> :
      <div className="d-flex">
        <input
          required
          type="datetime-local"
          className="form-control"
          value={data}
          onChange={(e) => setData(e.target.value)}
          name='data_limite_nova'
        />
        <button
          className="btn btn-secondary ml-1 p-0 text-center"
          style={{height: '38px', width: '25px'}}
          onClick={salvar}
        >
          <i className="fa fa-check" aria-hidden="true"></i>
        </button>
        <button
          className="btn btn-secondary ml-1 p-0 text-center"
          style={{height: '38px', width: '25px'}}
          onClick={() => {
            setEditarData(false)
            setData(dayjs(el.data_limite).format('YYYY-MM-DDTHH:mm'))
          }}
        >
          <i className="fa fa-close" aria-hidden="true"></i>
        </button>
      </div>
  )
}

// Sugestão de encerramento do clone: o dia seguinte na manual e a semana seguinte na
// Federal (mesmo dia e horário da extração), sempre no futuro. O backend valida.
const sugestaoClone = (rifa) => {
  const passo = rifa.forma_apuracao === 'federal' ? 7 : 1;
  let data = dayjs(rifa.data_limite).add(passo, 'day');

  while (data.isBefore(dayjs())) {
    data = data.add(passo, 'day');
  }

  return data.format('YYYY-MM-DDTHH:mm');
};

export default function Rifa() {
  const [credenciais, setCredenciais] = useState({});
  const [loading, setLoading] = useState(false);
  const [rifas, setRifas] = useState([]);
  const [extracoesFederais, setExtracoesFederais] = useState([]);
  const [alternando, setAlternando] = useState(null);

  useEffect(() => {
		buscarRifas()
		buscarExtracoesFederais()
  }, [])

  const buscarRifas = async () => {
    try {
      const { data: {data} } = await http.get('/rifas');
      setRifas(data);
    } catch (e) {
      avisarErro(e, 'Erro ao listar as rifas!')
    }
  }

  const buscarExtracoesFederais = async () => {
    try {
      const { data: {data} } = await http.get('/rifas/extracoes-federais');
      setExtracoesFederais(data ?? []);
    } catch (e) {
      // Sem permissão de criar rifa a lista não vem, e o formulário também não salva.
      setExtracoesFederais([]);
    }
  }

  const defineCredenciais = (ev) => {
    const {name, value} = ev.target;

    setCredenciais({
      ...credenciais
      , [name]: value
    })
  }

  const salvarRifa = async (e) => {
    e.preventDefault();

    const federal = credenciais.forma_apuracao === 'federal';
    const payload = {
      ...credenciais,
      forma_apuracao: federal ? 'federal' : 'manual',
      loteria_id: federal ? credenciais.loteria_id : null,
      valor: parseMoeda(credenciais.valor),
      premio: parseMoeda(credenciais.premio),
    };

    await toast.promise(
      http.post('/rifas', payload),
      {
        pending: {
          render(){
            setLoading(true)
            return 'Gerando numeros, aguarde!'
          }
        },
        success: {
          render({data}){
            setLoading(false)
            setCredenciais({});
            buscarRifas();
            document.forms[0].reset();
            return data.data.message
          }
        },
        error: {
          render({data}){
            setLoading(false)
            avisarErro(data, 'Erro ao criar a rifa!');
          }
        }
      }
    );
  }

  const alternarAtivo = async (rifa) => {
    setAlternando(rifa.id);
    try {
      const response = await http.patch('/rifas/' + rifa.id, { ativo: !rifa.ativo })
      alerta(response.data)
      buscarRifas()
    } catch (error) {
      avisarErro(error, 'Erro ao alterar a rifa!')
    } finally {
      setAlternando(null);
    }
  }

  const clonarRifa = async (rifa) => {
    const { value: dataLimite } = await Swal.fire({
      title: 'Clonar rifa',
      html:
        `<div style="text-align:left">
          <p>A cópia nasce <b>desativada</b>, com números novos. Revise e ative para vender.</p>
          <label>Encerramento</label>
          <input id="clone-data-limite" type="datetime-local" class="swal2-input" value="${sugestaoClone(rifa)}">
        </div>`,
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: 'Clonar',
      cancelButtonText: 'Voltar',
      preConfirm: () => document.getElementById('clone-data-limite').value,
    });

    if (!dataLimite) {
      return;
    }

    try {
      const response = await http.post('/rifas/' + rifa.id + '/clonar', { data_limite: dataLimite })
      alerta(response.data)
      buscarRifas()
    } catch (error) {
      avisarErro(error, 'Erro ao clonar a rifa!')
    }
  }

  const cancelaRifa = async (id) => {

    Swal.fire({
      title: 'Deseja cancelar a Rifa?',
      text: 'Só será possivel recuperar gerando novos numeros, cuidado ao cancelar!',
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      reverseButtons: true,
      confirmButtonText: 'Sim, cancelar!',
      cancelButtonText: 'Não',
    }).then(async (result) => {
      if (result.value) {
        try {
          const response = await http.delete('/rifas/'+id)
          buscarRifas()
          alerta(response.data)
        } catch (error) {
          avisarErro(error, 'Erro ao cancelar a rifa!')
        }
      }
    });

  }

  const federal = credenciais.forma_apuracao === 'federal';
  const extracaoEscolhida = extracoesFederais.find((extracao) => String(extracao.id) === String(credenciais.loteria_id));

  return (
    <>
      <div className="titulo-principal">Rifa</div>
      <form onSubmit={salvarRifa}>
        <div className="conteudo-principal row m-0">

          <div className="col-3 px-1">
            <label>Encerramento</label>
            <input
              required
              type="datetime-local"
              className="form-control"
              onChange={defineCredenciais}
              name='data_limite'
            />
          </div>

          <div className="col-3 px-1">
            <label>Nome/Titulo</label>
            <input
              required
              type="text"
              className="form-control"
              onChange={defineCredenciais}
              name='nome'
            />
          </div>

          <div className="col-3 px-1">
            <label>Faixa de números</label>
            <select
              required
              className="form-control"
              onChange={defineCredenciais}
              name="tipo"
              value={credenciais.tipo ?? ''}
            >
              <option value="" disabled>Selecione</option>
              {FAIXAS.map((faixa) => (
                <option key={faixa.valor} value={faixa.valor}>{faixa.rotulo} ({faixa.intervalo})</option>
              ))}
            </select>
          </div>

          <div className="col-3 px-1">
            <label>Forma de apuração</label>
            <select
              className="form-control"
              onChange={defineCredenciais}
              name="forma_apuracao"
              value={credenciais.forma_apuracao ?? 'manual'}
            >
              <option value="manual">Manual (lançar o número)</option>
              <option value="federal">Loteria Federal (automática)</option>
            </select>
          </div>

          {federal && (
            <div className="col-3 px-1 mt-2">
              <label>Extração da Federal</label>
              <select
                required
                className="form-control"
                onChange={defineCredenciais}
                name="loteria_id"
                value={credenciais.loteria_id ?? ''}
              >
                <option value="" disabled>
                  {extracoesFederais.length ? 'Selecione' : 'Nenhuma extração Federal ativa'}
                </option>
                {extracoesFederais.map((extracao) => (
                  <option key={extracao.id} value={extracao.id}>{extracao.nome}</option>
                ))}
              </select>
              {extracaoEscolhida && (
                <small className="text-muted">
                  Corre {diasDaExtracao(extracaoEscolhida)}; o encerramento vai até {extracaoEscolhida.hora.slice(0, 5)}.
                </small>
              )}
            </div>
          )}

          <div className="col-2 px-1 mt-2">
            <label>Valor do número</label>
            <input
              required
              type="text"
              inputMode="numeric"
              className="form-control"
              placeholder="0,00"
              value={credenciais.valor ?? ''}
              onChange={(e) => setCredenciais({...credenciais, valor: moedaInput(e.target.value)})}
            />
          </div>

          <div className="col-2 px-1 mt-2">
            <label>Premiação</label>
            <input
              required
              type="text"
              inputMode="numeric"
              className="form-control"
              placeholder="0,00"
              value={credenciais.premio ?? ''}
              onChange={(e) => setCredenciais({...credenciais, premio: moedaInput(e.target.value)})}
            />
          </div>

          <div className="col-2 px-1 mt-2">
            <PermissionGate permissions={['Criar rifa']}>
              <button
                type="submit"
                className={`btn btn-info w-100 mt-4`}
                style={{height: '38px'}}
                disabled={loading}
              >
                Salvar
              </button>
            </PermissionGate>
          </div>

        </div>
      </form>

      <div className="conteudo-principal p-0 mt-2">
        <div className="table-responsive">
          <table className="table text-left border text-center mb-0">
            <thead>
              <tr>
                <th>Criado em</th>
                <th>Encerramento</th>
                <th>Nome/Titulo</th>
                <th>Faixa</th>
                <th>Valor</th>
                <th>Prêmio</th>
                <th>Vendas</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {rifas.length === 0 && (
                <tr><td colSpan={9} className="text-muted p-4">Nenhuma rifa cadastrada.</td></tr>
              )}

              {rifas.map((rifa) => {
                const status = ROTULO_STATUS[rifa.status] ?? { texto: rifa.status, classe: 'badge-light' };
                const vendidos = (rifa.total_numeros ?? 0) - (rifa.disponiveis ?? 0);
                const podeAlternar = rifa.status === 'em_andamento' || rifa.status === 'desativada';

                return (
                  <tr key={rifa.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{dayjs(rifa.created_at).format('DD/MM/YY HH:mm')}</td>
                    <td style={{ maxWidth: "180px" }}>
                      <CampoEncerramento el={rifa} aoAlterar={buscarRifas} />
                    </td>
                    <td>{rifa.nome}</td>
                    <td>
                      {ROTULO_FAIXA[rifa.tipo] ?? rifa.tipo}
                      <div className="small text-muted">
                        {rifa.forma_apuracao === 'federal' ? `Federal · ${rifa.loteria?.nome ?? ''}` : 'Manual'}
                      </div>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatMoney(rifa.valor)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatMoney(rifa.premio)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {/* Rifa cancelada perde os números: a conta daria tudo vendido. */}
                      {rifa.cancelado_em ? '—' : `${vendidos} / ${rifa.total_numeros}`}
                      <div className="small text-muted">{formatMoney(rifa.vendas)}</div>
                    </td>
                    <td>
                      <span className={`badge ${status.classe}`}>{status.texto}</span>
                      {podeAlternar && (
                        <PermissionGate permissions={['Alterar rifa']}>
                          <button
                            type="button"
                            className={`btn btn-sm btn-${rifa.ativo ? 'outline-secondary' : 'outline-success'} d-block mx-auto mt-1 px-1`}
                            disabled={alternando === rifa.id}
                            onClick={() => alternarAtivo(rifa)}
                          >
                            {alternando === rifa.id ? '...' : (rifa.ativo ? 'Desativar' : 'Ativar')}
                          </button>
                        </PermissionGate>
                      )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <PermissionGate permissions={['Criar rifa']}>
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          title="Clonar rifa"
                          onClick={() => clonarRifa(rifa)}
                        >
                          <i className="fa fa-copy" aria-hidden="true" />
                        </button>
                      </PermissionGate>
                      <PermissionGate permissions={['Remover rifa']}>
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-danger"
                          title="Cancelar rifa"
                          disabled={!!rifa.cancelado_em}
                          onClick={() => cancelaRifa(rifa.id)}
                        >
                          <i className="fa fa-times" aria-hidden="true" />
                        </button>
                      </PermissionGate>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}
